import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Target, CheckCircle2, Users, ChevronRight, Loader2, TrendingUp, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/Layout";

function StatCard({ icon: Icon, label, value, sub, testid, accent, blue }) {
  const ring = blue ? "bg-[#1D4ED8]/15" : accent ? "bg-[#D50A0A]/10" : "bg-white/5";
  const ic = blue ? "text-[#4C8DFF]" : accent ? "neon-text" : "text-zinc-400";
  return (
    <div data-testid={testid} className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-6">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-4 ${ring}`}>
        <Icon className={`h-5 w-5 ${ic}`} />
      </div>
      <div className="font-display text-4xl font-black tracking-tight">{value}</div>
      <div className="text-zinc-400 text-sm mt-1">{label}</div>
      {sub && <div className="text-zinc-600 text-xs mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [weekLabel, setWeekLabel] = useState("");
  const [games, setGames] = useState([]);

  useEffect(() => {
    (async () => {
      const [dash, season] = await Promise.all([api.get("/dashboard"), api.get("/season/current")]);
      setData(dash.data);
      const wl = season.data.weeks.find((w) => w.week === dash.data.current_week);
      setWeekLabel(wl?.label || `Week ${dash.data.current_week}`);
      if (dash.data.current_week != null) {
        try {
          const g = await api.get(`/weeks/${dash.data.current_week}/games`);
          setGames(g.data);
        } catch { setGames([]); }
      }
    })();
  }, []);

  if (!data) {
    return <PageShell testid="dashboard-page"><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 neon-text animate-spin" /></div></PageShell>;
  }

  return (
    <PageShell testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="overline">{weekLabel} · Season {new Date().getFullYear()}</span>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">
            Welcome, {user?.display_name || user?.username}
          </h1>
        </div>
        <Link to="/picks" data-testid="dashboard-make-picks-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D50A0A] text-white font-bold hover:shadow-[0_0_18px_rgba(213,10,10,0.4)] active:scale-95 transition-transform">
          Make Your Picks <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-rank" icon={Trophy} blue label="League Rank"
          value={data.rank ? `#${data.rank}` : "—"} sub={`of ${data.total_players} players`} />
        <StatCard testid="stat-points" icon={TrendingUp} accent label="Season Points" value={data.season_points} />
        <StatCard testid="stat-correct" icon={CheckCircle2} label="Correct Picks" value={data.correct_picks} />
        <StatCard testid="stat-week-picks" icon={Target} label="Picks This Week"
          value={`${data.picks_made_this_week}/${data.games_this_week}`} sub={weekLabel} />
      </div>

      <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-6 mb-6" data-testid="dashboard-schedule">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#4C8DFF]" />
            <h2 className="font-display text-xl font-bold">This Week's Games</h2>
            <span className="text-xs text-zinc-500">· {weekLabel}</span>
          </div>
          <Link to="/picks" data-testid="schedule-make-picks-link" className="text-sm text-[#4C8DFF] hover:underline flex items-center gap-1">
            Make picks <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        {games.length === 0 ? (
          <p className="text-zinc-500 text-sm py-4">No games scheduled yet for this week.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {games.map((g) => {
              const kick = g.kickoff ? new Date(g.kickoff) : null;
              const status = g.status === "final" ? "FINAL" : g.status === "inprogress" ? "LIVE"
                : g.locked ? "LOCKED"
                : kick ? kick.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "TBD";
              return (
                <div key={g.id} data-testid={`schedule-game-${g.away}-${g.home}`}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#141416] bg-[#0A0A0A]">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <img src={g.away_team?.logo} alt={g.away} className="h-6 w-6 object-contain" onError={(e)=>{e.currentTarget.style.visibility="hidden";}} />
                    <span>{g.away}</span>
                    {g.status === "final" && <span className="text-zinc-500 text-xs">{g.away_score}</span>}
                    <span className="text-zinc-600 text-xs">@</span>
                    <img src={g.home_team?.logo} alt={g.home} className="h-6 w-6 object-contain" onError={(e)=>{e.currentTarget.style.visibility="hidden";}} />
                    <span>{g.home}</span>
                    {g.status === "final" && <span className="text-zinc-500 text-xs">{g.home_score}</span>}
                  </div>
                  <span className={`text-[11px] font-bold ${g.status === "inprogress" ? "text-red-400" : g.locked ? "text-zinc-500" : "text-[#4C8DFF]"}`}>{status}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#4C8DFF]" />
            <h2 className="font-display text-xl font-bold">Top of the Board</h2>
          </div>
          <Link to="/leaderboard" className="text-sm text-[#4C8DFF] hover:underline flex items-center gap-1">
            Full leaderboard <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="space-y-2">
          {data.leaderboard_top.map((row) => (
            <div key={row.user_id} data-testid={`dash-lb-${row.rank}`}
              className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                row.user_id === user?.id ? "border-[#D50A0A]/50 bg-[#D50A0A]/5" : "border-[#1F1F22] bg-[#0A0A0A]"
              }`}>
              <div className="flex items-center gap-4">
                <span className={`font-display font-black text-lg w-6 ${row.rank <= 3 ? "neon-text" : "text-zinc-500"}`}>{row.rank}</span>
                <span className="font-semibold text-sm">{row.display_name}{row.user_id === user?.id && <span className="text-zinc-500"> (you)</span>}</span>
              </div>
              <div className="flex items-center gap-4">
                {row.record != null && <span className="text-zinc-300 text-sm font-semibold">{row.record}</span>}
                <span className="font-display font-bold">{row.points} <span className="text-zinc-500 text-xs font-normal">pts</span></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
